/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { PostalAddress } from './PostalAddress';
import {
    PostalAddressFromJSON,
    PostalAddressFromJSONTyped,
    PostalAddressToJSON,
    PostalAddressToJSONTyped,
} from './PostalAddress';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { Party } from './Party';
import {
    PartyFromJSON,
    PartyFromJSONTyped,
    PartyToJSON,
    PartyToJSONTyped,
} from './Party';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { InvoiceLine } from './InvoiceLine';
import {
    InvoiceLineFromJSON,
    InvoiceLineFromJSONTyped,
    InvoiceLineToJSON,
    InvoiceLineToJSONTyped,
} from './InvoiceLine';
import type { InvoiceTax } from './InvoiceTax';
import {
    InvoiceTaxFromJSON,
    InvoiceTaxFromJSONTyped,
    InvoiceTaxToJSON,
    InvoiceTaxToJSONTyped,
} from './InvoiceTax';
import type { Terms } from './Terms';
import {
    TermsFromJSON,
    TermsFromJSONTyped,
    TermsToJSON,
    TermsToJSONTyped,
} from './Terms';
import type { Value } from './Value';
import {
    ValueFromJSON,
    ValueFromJSONTyped,
    ValueToJSON,
    ValueToJSONTyped,
} from './Value';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
    DocumentToJSONTyped,
} from './Document';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';

/**
 * Response schema for InvoicingDocument logistics object.
 * @export
 * @interface InvoicingDocumentResponse
 */
export interface InvoicingDocumentResponse {
    /**
     * TODO: to be reviewed to comply with EN-16931.
     * @type {string}
     * @memberof InvoicingDocumentResponse
     */
    documentType?: string;
    /**
     * TODO: to be reviewed to comply with EN-16931.
     * @type {Date}
     * @memberof InvoicingDocumentResponse
     */
    dueDate?: Date;
    /**
     * TODO: to be reviewed to comply with EN-16931.
     * @type {Array<string>}
     * @memberof InvoicingDocumentResponse
     */
    footerTexts?: Array<string>;
    /**
     * TODO: to be reviewed to comply with EN-16931.
     * @type {Date}
     * @memberof InvoicingDocumentResponse
     */
    invoiceDate?: Date;
    /**
     * 
     * @type {PostalAddress}
     * @memberof InvoicingDocumentResponse
     */
    address?: PostalAddress;
    /**
     * List of contacts associated with the invoice document. These can be individuals or teams who can be contacted about the invoice document.
     * @type {Array<Contact>}
     * @memberof InvoicingDocumentResponse
     */
    contacts?: Array<Contact>;
    /**
     * List of documents related to an invoice, such as the invoice itself, credit notes, receipts, and any other related documents.
     * @type {Array<Document>}
     * @memberof InvoicingDocumentResponse
     */
    documents?: Array<Document>;
    /**
     * External references of the invoicing document should be used when they are defined in a legacy system registering the invoicing document under another reference identifier than the URI. This is useful for searching invoicing documents using the ID from the legacy system.
     *                                      Common ExternalReference Types include 'PROVIDER_ID' and 'CUSTOMER_ID'.
     * @type {Array<ExternalReference>}
     * @memberof InvoicingDocumentResponse
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * 
     * @type {Array<InvoiceLine>}
     * @memberof InvoicingDocumentResponse
     */
    invoiceLines?: Array<InvoiceLine>;
    /**
     * 
     * @type {Array<InvoiceTax>}
     * @memberof InvoicingDocumentResponse
     */
    invoiceTaxes?: Array<InvoiceTax>;
    /**
     * 
     * @type {Party}
     * @memberof InvoicingDocumentResponse
     */
    invoicedParty?: Party;
    /**
     * 
     * @type {Party}
     * @memberof InvoicingDocumentResponse
     */
    invoicingParty?: Party;
    /**
     * Special characteristics applied to the invoicing document and to be shared between provider and customer.
     * @type {Array<SpecialCharacteristic>}
     * @memberof InvoicingDocumentResponse
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * Special instructions applied to the invoicing document and to be shared with a provider.
     * @type {Array<SpecialInstruction>}
     * @memberof InvoicingDocumentResponse
     */
    specialInstructions?: Array<SpecialInstruction>;
    /**
     * 
     * @type {Array<Terms>}
     * @memberof InvoicingDocumentResponse
     */
    terms?: Array<Terms>;
    /**
     * 
     * @type {Value}
     * @memberof InvoicingDocumentResponse
     */
    totalAmount?: Value;
    /**
     * 
     * @type {Value}
     * @memberof InvoicingDocumentResponse
     */
    totalAmountIncludingTaxes?: Value;
    /**
     * Internal unique identifier of the resource. Id can have different formats like UUID v4, UUID v7, or any other string calculated.
     * @type {string}
     * @memberof InvoicingDocumentResponse
     */
    id?: string;
}

/**
 * Check if a given object implements the InvoicingDocumentResponse interface.
 */
export function instanceOfInvoicingDocumentResponse(value: object): value is InvoicingDocumentResponse {
    return true;
}

export function InvoicingDocumentResponseFromJSON(json: any): InvoicingDocumentResponse {
    return InvoicingDocumentResponseFromJSONTyped(json, false);
}

export function InvoicingDocumentResponseFromJSONTyped(json: any, ignoreDiscriminator: boolean): InvoicingDocumentResponse {
    if (json == null) {
        return json;
    }
    return {
        
        'documentType': json['documentType'] == null ? undefined : json['documentType'],
        'dueDate': json['dueDate'] == null ? undefined : (new Date(json['dueDate'])),
        'footerTexts': json['footerTexts'] == null ? undefined : json['footerTexts'],
        'invoiceDate': json['invoiceDate'] == null ? undefined : (new Date(json['invoiceDate'])),
        'address': json['address'] == null ? undefined : PostalAddressFromJSON(json['address']),
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'documents': json['documents'] == null ? undefined : ((json['documents'] as Array<any>).map(DocumentFromJSON)),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'invoiceLines': json['invoiceLines'] == null ? undefined : ((json['invoiceLines'] as Array<any>).map(InvoiceLineFromJSON)),
        'invoiceTaxes': json['invoiceTaxes'] == null ? undefined : ((json['invoiceTaxes'] as Array<any>).map(InvoiceTaxFromJSON)),
        'invoicedParty': json['invoicedParty'] == null ? undefined : PartyFromJSON(json['invoicedParty']),
        'invoicingParty': json['invoicingParty'] == null ? undefined : PartyFromJSON(json['invoicingParty']),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
        'terms': json['terms'] == null ? undefined : ((json['terms'] as Array<any>).map(TermsFromJSON)),
        'totalAmount': json['totalAmount'] == null ? undefined : ValueFromJSON(json['totalAmount']),
        'totalAmountIncludingTaxes': json['totalAmountIncludingTaxes'] == null ? undefined : ValueFromJSON(json['totalAmountIncludingTaxes']),
        'id': json['id'] == null ? undefined : json['id'],
    };
}

export function InvoicingDocumentResponseToJSON(json: any): InvoicingDocumentResponse {
    return InvoicingDocumentResponseToJSONTyped(json, false);
}

export function InvoicingDocumentResponseToJSONTyped(value?: InvoicingDocumentResponse | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'documentType': value['documentType'],
        'dueDate': value['dueDate'] == null ? value['dueDate'] : value['dueDate'].toISOString(),
        'footerTexts': value['footerTexts'],
        'invoiceDate': value['invoiceDate'] == null ? value['invoiceDate'] : value['invoiceDate'].toISOString(),
        'address': PostalAddressToJSON(value['address']),
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'documents': value['documents'] == null ? undefined : ((value['documents'] as Array<any>).map(DocumentToJSON)),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'invoiceLines': value['invoiceLines'] == null ? undefined : ((value['invoiceLines'] as Array<any>).map(InvoiceLineToJSON)),
        'invoiceTaxes': value['invoiceTaxes'] == null ? undefined : ((value['invoiceTaxes'] as Array<any>).map(InvoiceTaxToJSON)),
        'invoicedParty': PartyToJSON(value['invoicedParty']),
        'invoicingParty': PartyToJSON(value['invoicingParty']),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
        'terms': value['terms'] == null ? undefined : ((value['terms'] as Array<any>).map(TermsToJSON)),
        'totalAmount': ValueToJSON(value['totalAmount']),
        'totalAmountIncludingTaxes': ValueToJSON(value['totalAmountIncludingTaxes']),
        'id': value['id'],
    };
}

